import { findScrollableParent } from '@thiememeulenhoff/js-sdk';
import { border2, token, TmElement } from '@thiememeulenhoff/lit-sdk';
import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../../helpers/element.helper';
import { DraggableComponentEvent } from '../shared/events/drag-drop-component.event';
import {
  DRAG_TRIGGER_REGISTERED_EVENT,
  DRAGGABLE_RELEASED,
  DRAGGABLE_SELECTED,
} from '../shared/events/drag-drop.events';
import { DragDropService } from '../shared/services/drag-drop.service';
import { DragCallback } from '../shared/types/dragg-callback.type';

const draggableStyle = [
  css`
    :host(:focus) {
      outline: none;
    }

    :host(.selected) {
      --draggable-option-background: ${token('draggable-background-selected')};
      --draggable-option-border: ${border2(2, 'draggable-border-selected')};
    }

    ::slotted(.focus-outline) {
      border: ${border2(2, 'draggable-border-focus')};
    }
  `,
];

type Position = { x: number; y: number };

@customElement(tagName('draggable'))
export class DraggableComponent extends TmElement {
  static override styles = draggableStyle;

  dragging = false;

  clicked = false;

  #index = 0;

  // @Todo, make this configurable and get it from the styles
  #gap = 10;

  #position: Position = { x: 0, y: 0 };

  get startPosition(): Position {
    return this.#position;
  }

  get disabled() {
    return this.#disabled;
  }

  triggers?: Array<HTMLElement>;

  #height = 0;

  #width = 0;

  #draggable?: Array<Draggable>;

  #initialized = false;

  #scrollableYPosition?: number;

  onPressFn?: DragCallback;

  onDragFn?: DragCallback;

  onReleaseFn?: DragCallback;

  @property({ reflect: true, attribute: true })
  value: unknown;

  @property({ attribute: true })
  dragIndex?: number = undefined;

  @property({ type: Boolean, attribute: 'keyboard-accessible' })
  keyboardAccessible = false;

  @property({ type: Boolean, attribute: 'has-duplicates' })
  hasDuplicates = false;

  wasDuplicate = false;

  duplicate: DraggableComponent | null = null;

  private resizeObserver = new ResizeObserver((entry) => {
    this.#height = entry[0].contentRect.height + this.#gap;
    this.#width = entry[0].contentRect.width + this.#gap;
  });

  get height() {
    return this.#height;
  }

  get width() {
    return this.#width;
  }

  get index() {
    return this.#index;
  }

  setIndex(index: number) {
    this.#index = index;
  }

  protected override onCreate = () => {
    if (this.#isSafari()) {
      this.addEventListener('mouseenter', this.#handleMouseEnter);
    }
  };

  #handleMouseEnter() {
    this.#scrollableYPosition = findScrollableParent(this)?.scrollTop;
  }

  createDuplicate() {
    const duplicate = this.cloneNode(true) as DraggableComponent;
    duplicate.style.position = 'absolute';
    this.parentElement?.appendChild(duplicate);
    return duplicate;
  }

  onPress(event: PointerEvent) {
    gsap.to(this, {
      duration: DragDropService.ANIMATION_TIME_IN_SECONDS,
      scale: DragDropService.ANIMATION_SCALE,
      zIndex: '1000',
    });

    if (this.hasDuplicates) {
      this.duplicate = this.createDuplicate();
      this.duplicate.initializeDraggable(true);
    }

    if (this.onPressFn) {
      this.onPressFn(this, event);
    }

    // We hate this piece of (****) code but it's needed to prevent the safari scroll to top bug
    if (this.#isSafari()) {
      const scrollableParent = findScrollableParent(this);
      if (scrollableParent) {
        scrollableParent.scrollTop = this.#scrollableYPosition || 0;
      }
    }
  }

  #isSafari(): boolean {
    const ua = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(ua);
  }

  onDrag(event: PointerEvent) {
    if (this.onDragFn) {
      this.dragging = true;
      this.onDragFn(this, event);
    }
  }

  onRelease(event: PointerEvent) {
    this.dispatchEvent(new DraggableComponentEvent(DRAGGABLE_RELEASED, this));
    gsap.to(this, {
      duration: DragDropService.ANIMATION_TIME_IN_SECONDS,
      scale: 1,
      zIndex: '0',
    });

    if (!this.dragging) {
      this.clicked = true;
      this.dispatchGlobalEvent({
        eventName: DRAGGABLE_SELECTED,
        detail: this,
        bubbles: true,
      });
    }

    this.dragging = false;

    if (this.duplicate) {
      this.duplicate.style.position = 'initial';
      this.#setDuplicateInitialPosition();
    }

    if (this.onReleaseFn) {
      this.onReleaseFn(this, event);
    }
  }

  #setDuplicateInitialPosition() {
    const observer = new MutationObserver((mutationsList, observer) => {
      mutationsList.forEach(() => {
        if (this.parentElement?.children.length === 0 && this.duplicate) {
          this.duplicate.style.position = 'initial';
          observer.disconnect();
        }
      });
    });

    if (this.parentElement)
      observer.observe(this.parentElement, { childList: true });
  }

  resetPosition() {
    gsap.to(this, {
      duration: DragDropService.ANIMATION_TIME_IN_SECONDS,
      ...this.#position,
    });
  }

  updatePosition(position: Position) {
    this.#position = { ...position };
    this.resetPosition();
  }

  registerEvents({
    onPress,
    onDrag,
    onRelease,
  }: {
    onPress?: DragCallback;
    onDrag?: DragCallback;
    onRelease?: DragCallback;
  }) {
    if (onPress) {
      this.onPressFn = onPress;
    }

    if (onDrag) {
      this.onDragFn = onDrag;
    }

    if (onRelease) {
      this.onReleaseFn = onRelease;
    }
  }

  clearEvents() {
    this.onPressFn = undefined;
    this.onDragFn = undefined;
    this.onReleaseFn = undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initializeDraggable(standalone = false, skipIfInitialized = false) {
    if (skipIfInitialized && this.#initialized) {
      return;
    }

    if (this.#draggable) {
      this.#draggable.forEach((draggable) => draggable.kill());
    }

    if (!this.#disabled) {
      this.#initialized = true;
      this.#draggable = Draggable.create(this, {
        onDrag: this.onDrag.bind(this),
        onRelease: this.onRelease.bind(this),
        onPress: this.onPress.bind(this),
        trigger: this.triggers,
        autoScroll: 1,
        autoScrollMarginTop: DragDropService.SCROLL_MARGIN,
        autoScrollMarginBottom: DragDropService.SCROLL_MARGIN,
      });
    }
  }

  disableDraggable() {
    if (this.#draggable) {
      this.#draggable.forEach((draggable) => draggable.kill());
    }
  }

  #initializeTimeout?: number;

  protected override async firstUpdated() {
    // this should probably be handled with a "await this.updateComplete;"
    // but doing so breaks drag-triggers
    this.#initializeTimeout = window.setTimeout(() => {
      this.initializeDraggable(false, true);
    }, 100);

    this.shadowRoot?.addEventListener(
      DRAG_TRIGGER_REGISTERED_EVENT,
      (e: Event) => {
        e.stopPropagation();
        if (this.triggers === undefined) {
          this.triggers = [];
        }

        if (
          e.target &&
          this.#isHTMLElement(e.target) &&
          !this.triggers.includes(e.target)
        ) {
          clearTimeout(this.#initializeTimeout);

          this.triggers.push(e.target);

          this.#initializeTimeout = window.setTimeout(() => {
            this.initializeDraggable(false);
          }, 100);
        }
      },
    );

    this.resizeObserver.observe(this);
  }

  #isHTMLElement(input: EventTarget): input is HTMLElement {
    return 'innerHTML' in input;
  }

  override connectedCallback() {
    super.connectedCallback();

    if (this.keyboardAccessible && !this.#disabled) {
      this.setAttribute('tabindex', '0');
      this.addEventListener('keydown', this.#handleKeyDown);
      this.addEventListener('focus', this.#handleFocus);
      this.addEventListener('blur', this.#handleBlur);
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('focus', this.#handleFocus);
    this.removeEventListener('blur', this.#handleBlur);

    if (this.#draggable) {
      this.#draggable.forEach((drag) => drag.kill());
    }
  }

  #handleFocus() {
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot) {
      slot.assignedNodes().forEach((node) => {
        if (node instanceof HTMLElement) {
          node.classList.add('focus-outline');
        }
      });
    }
  }

  #handleBlur() {
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot) {
      slot.assignedNodes().forEach((node) => {
        if (node instanceof HTMLElement) {
          node.classList.remove('focus-outline');
        }
      });
    }
  }

  #handleKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space' && event.target === this) {
      event.preventDefault();
      event.stopPropagation();
      this.dispatchGlobalEvent({
        eventName: DRAGGABLE_SELECTED,
        detail: this,
        bubbles: true,
      });
    }
  }

  setSelected(selected: boolean) {
    if (selected) {
      this.classList.add('selected');
    } else {
      this.classList.remove('selected');
    }
  }

  show() {
    this.style.opacity = '1';
  }

  hide() {
    // we use opacity to keep it's height.
    this.style.opacity = '0';
  }

  #disabled = false;

  public disablePress() {
    this.removeAttribute('tabindex');
    this.#disabled = true;
    this.disableDraggable();
  }

  public enablePress() {
    this.setAttribute('tabindex', '0');
    this.#disabled = false;
    this.initializeDraggable();
  }

  static isDraggableComponent(input: Node): input is DraggableComponent {
    return 'dragging' in input;
  }

  override render() {
    return html` <slot></slot> `;
  }

  setWasDuplicate() {
    this.wasDuplicate = true;
    this.hasDuplicates = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-draggable': DraggableComponent;
  }
}
